import React from 'react';
import {
  PencilIcon,
  TrashIcon,
  StarIcon,
  CalendarIcon,
  TagIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { Photo, getPhotoUrl, formatFileSize } from '../../api/photos';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface ViewPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  photo: Photo | null;
  onEdit: () => void;
  onEditImage?: () => void;
  onDelete: () => void;
  onSetAvatar: (photo: Photo) => void;
}

const ViewPhotoModal: React.FC<ViewPhotoModalProps> = ({
  isOpen,
  onClose,
  photo,
  onEdit,
  onEditImage,
  onDelete,
  onSetAvatar,
}) => {
  if (!photo) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = getPhotoUrl(photo.url);
    link.download = photo.filename || 'photo.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="flex items-center gap-3">
            <DocumentIcon className="h-6 w-6 shrink-0 text-slate-400" />
            <span className="min-w-0">{photo.caption || 'Photo Details'}</span>
          </span>
        }
        subtitle={photo.isAvatar ? 'Avatar Photo' : undefined}
        onClose={onClose}
      />
      <BrandModalBody>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Photo Display */}
              <div className="space-y-4">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={getPhotoUrl(photo.url)}
                    alt={photo.caption || 'Photo'}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {onEditImage && (
                    <button
                      onClick={onEditImage}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Edit image
                    </button>
                  )}
                  <button
                    onClick={onEdit}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit details
                  </button>

                  {!photo.isAvatar && (
                    <button
                      onClick={() => onSetAvatar(photo)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                    >
                      <StarIcon className="h-4 w-4 mr-2" />
                      Set as Avatar
                    </button>
                  )}

                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download
                  </button>

                  <button
                    onClick={onDelete}
                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </div>
              </div>

              {/* Photo Information */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-sm font-medium text-slate-900 mb-3">Basic Information</h4>
                  <dl className="space-y-2">
                    {photo.caption && (
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Caption</dt>
                        <dd className="text-sm text-slate-900">{photo.caption}</dd>
                      </div>
                    )}

                    <div>
                      <dt className="text-sm font-medium text-slate-500">Created</dt>
                      <dd className="text-sm text-slate-900 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1 text-slate-400" />
                        {formatDate(photo.createdAt)}
                      </dd>
                    </div>

                    {photo.updatedAt !== photo.createdAt && (
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Last Updated</dt>
                        <dd className="text-sm text-slate-900 flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1 text-slate-400" />
                          {formatDate(photo.updatedAt)}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Associations */}
                {(photo.birdId || photo.speciesId) && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Associations</h4>
                    <dl className="space-y-2">
                      {photo.birdId && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Bird ID</dt>
                          <dd className="text-sm text-slate-900">{photo.birdId}</dd>
                        </div>
                      )}

                      {photo.speciesId && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Species ID</dt>
                          <dd className="text-sm text-slate-900">{photo.speciesId}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {/* Tags */}
                {photo.tags && photo.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3 flex items-center">
                      <TagIcon className="h-4 w-4 mr-1" />
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {photo.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* File Information */}
                {photo.metadata && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">File Information</h4>
                    <dl className="space-y-2">
                      {photo.filename && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Filename</dt>
                          <dd className="text-sm text-slate-900">{photo.filename}</dd>
                        </div>
                      )}

                      {photo.metadata.originalName && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Original Name</dt>
                          <dd className="text-sm text-slate-900">{photo.metadata.originalName}</dd>
                        </div>
                      )}

                      {photo.metadata.mimeType && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Type</dt>
                          <dd className="text-sm text-slate-900">{photo.metadata.mimeType}</dd>
                        </div>
                      )}

                      {photo.metadata.size && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Size</dt>
                          <dd className="text-sm text-slate-900">{formatFileSize(photo.metadata.size)}</dd>
                        </div>
                      )}

                      {photo.metadata.width && photo.metadata.height && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Dimensions</dt>
                          <dd className="text-sm text-slate-900">
                            {photo.metadata.width} × {photo.metadata.height} pixels
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>
            </div>
      </BrandModalBody>

      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
          Close
        </button>
        <button type="button" onClick={onEdit} className="btn btn-primary text-sm">
          Edit details
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
};

export default ViewPhotoModal; 